import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const AdminTrackingDashboard = () => {
  const navigate = useNavigate();
  const adminUser = useMemo(() => JSON.parse(localStorage.getItem('adminUser') || '{}'), []);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const loadTracking = async () => {
    setLoading(true);
    try {
      const headers = { 'admin-email': adminUser.email };
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (roleFilter) params.set('userRole', roleFilter);
      if (typeFilter) params.set('type', typeFilter);

      const [summaryRes, eventsRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/tracking/summary`, { headers }),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/tracking/events?${params.toString()}`, { headers })
      ]);

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (eventsRes.ok) setEvents(await eventsRes.json());
    } catch (error) {
      console.error('Error loading tracking dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!adminUser?.email) {
      navigate('/admin');
      return;
    }
    loadTracking();
  }, []);

  const statCards = [
    ['Total Events', summary?.totals?.events || 0, 'All recorded activity'],
    ['24h Events', summary?.totals?.events24h || 0, 'Recent activity'],
    ['Visitors', summary?.totals?.visitors || 0, 'Unique browser/device IDs'],
    ['Staff Events', summary?.totals?.staffEvents || 0, 'Admin, teacher, staff, security']
  ];

  if (loading && !summary) {
    return <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-700 text-xl">Loading tracking dashboard...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <p className="text-cyan-700 font-black">Creative Town Tracking</p>
            <h1 className="text-3xl font-black text-slate-950 mt-1">Visitor and Staff Activity</h1>
            <p className="text-slate-600 mt-2">High-level website, campaign, dashboard, and account activity tracking.</p>
          </div>
          <div className="flex gap-3">
            <button onClick={loadTracking} className="bg-cyan-700 text-white px-5 py-3 rounded-lg font-bold hover:bg-cyan-800">Refresh</button>
            <button onClick={() => navigate('/dashboard')} className="bg-slate-700 text-white px-5 py-3 rounded-lg font-bold hover:bg-slate-800">Back</button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {statCards.map(([label, value, detail]) => (
            <div key={label} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <p className="text-sm font-bold text-slate-500">{label}</p>
              <p className="text-3xl font-black text-slate-950 mt-2">{Number(value).toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-1">{detail}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
          {[
            ['Top Pages', summary?.topPages || []],
            ['Top Actions', summary?.topActions || []],
            ['Top Sources', summary?.topSources || []]
          ].map(([title, rows]) => (
            <div key={title} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h2 className="font-black text-slate-900 mb-4">{title}</h2>
              <div className="space-y-3">
                {rows.map((row) => (
                  <div key={row.label} className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate text-slate-700" title={row.label}>{row.label}</span>
                    <b className="text-slate-950">{row.count}</b>
                  </div>
                ))}
                {!rows.length && <p className="text-sm text-slate-500">No data yet.</p>}
              </div>
            </div>
          ))}
        </div>

        <section className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm mb-8">
          <h2 className="font-black text-slate-900 mb-4">Staff Account Activity</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Role</th>
                  <th className="py-2 pr-4">Events</th>
                  <th className="py-2 pr-4">Last Action</th>
                  <th className="py-2 pr-4">Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.staffActivity || []).map((user) => (
                  <tr key={`${user.userRole}-${user.userId}-${user.userName}`} className="border-b last:border-0">
                    <td className="py-3 pr-4 font-bold text-slate-900">{user.userName}</td>
                    <td className="py-3 pr-4 capitalize">{user.userRole}</td>
                    <td className="py-3 pr-4">{user.count}</td>
                    <td className="py-3 pr-4">{user.lastAction}</td>
                    <td className="py-3 pr-4">{new Date(user.lastSeen).toLocaleString()}</td>
                  </tr>
                ))}
                {!summary?.staffActivity?.length && (
                  <tr><td className="py-4 text-slate-500" colSpan="5">No staff activity recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3 mb-5">
            <label className="flex-1">
              <span className="block text-sm font-bold text-slate-700 mb-1">Search</span>
              <input value={query} onChange={(e) => setQuery(e.target.value)} className="w-full border rounded-lg px-3 py-2" placeholder="User, page, action, source..." />
            </label>
            <label>
              <span className="block text-sm font-bold text-slate-700 mb-1">Role</span>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="border rounded-lg px-3 py-2">
                <option value="">All roles</option>
                <option value="visitor">Visitor</option>
                <option value="admin">Admin</option>
                <option value="teacher">Teacher</option>
                <option value="staff">Staff</option>
                <option value="parent">Parent</option>
                <option value="student">Student</option>
                <option value="security">Security</option>
              </select>
            </label>
            <label>
              <span className="block text-sm font-bold text-slate-700 mb-1">Type</span>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="border rounded-lg px-3 py-2">
                <option value="">All types</option>
                <option value="page_view">Page views</option>
                <option value="click">Clicks</option>
                <option value="form_submit">Forms</option>
                <option value="api_request">API requests</option>
              </select>
            </label>
            <button onClick={loadTracking} className="bg-slate-900 text-white px-5 py-2 rounded-lg font-bold">Apply</button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-4">Time</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Action</th>
                  <th className="py-2 pr-4">Path</th>
                  <th className="py-2 pr-4">Source</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className="border-b last:border-0 align-top">
                    <td className="py-3 pr-4 whitespace-nowrap">{new Date(event.createdAt).toLocaleString()}</td>
                    <td className="py-3 pr-4">{event.type}</td>
                    <td className="py-3 pr-4">
                      <b>{event.userName || event.userId || 'Visitor'}</b>
                      <span className="block text-xs text-slate-500">{event.userRole || 'visitor'}</span>
                    </td>
                    <td className="py-3 pr-4">{event.label || event.action}</td>
                    <td className="py-3 pr-4 max-w-xs truncate" title={event.path || event.pageUrl}>{event.path || event.pageUrl}</td>
                    <td className="py-3 pr-4 max-w-xs truncate" title={event.utm?.source || event.referrer}>{event.utm?.source || event.referrer || 'direct'}</td>
                  </tr>
                ))}
                {!events.length && (
                  <tr><td className="py-4 text-slate-500" colSpan="6">No matching events yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminTrackingDashboard;
